import { useState } from "react";
import { GlassCard, Btn, PillTag } from "./UI";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type DayOfWeek,
} from "@/lib/store";

/**
 * SabbathToggle — settings block where a sister chooses her weekly Sabbath.
 * No leaderboard, no shame. The Sabbath is a gift, not a metric. Streaks
 * pause when she rests. Devotional shows the day's reflection automatically.
 */
interface Props {
  userId: string;
}

const DAYS: Array<{ value: DayOfWeek; short: string; long: string }> = [
  { value: 0, short: "Sun", long: "Sunday" },
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
];

export default function SabbathToggle({ userId }: Props) {
  const current = useStoreQuery(() => queries.sabbathFor(userId));
  const next = useStoreQuery(() => queries.nextSabbath(userId));
  const isToday = useStoreQuery(() => queries.isSabbathToday(userId));
  const save = useStoreMutation(mutations.setSabbathDay);

  const [enabled, setEnabled] = useState<boolean>(current?.enabled ?? true);
  const [day, setDay] = useState<DayOfWeek>((current?.dayOfWeek ?? 0) as DayOfWeek);
  const [pauseStreaks, setPauseStreaks] = useState<boolean>(current?.pauseStreaks ?? true);
  const [devotional, setDevotional] = useState<boolean>(current?.devotionalReminder ?? true);
  const [savedFlash, setSavedFlash] = useState<boolean>(false);

  const onSave = () => {
    save({
      userId,
      enabled,
      dayOfWeek: day,
      pauseStreaks,
      devotionalReminder: devotional,
    });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };

  const dayLabel = DAYS.find((d) => d.value === day)?.long ?? "Sunday";
  const trailingNote = isToday
    ? "Resting today"
    : next && next.daysAway > 0
      ? `Next rest in ${next.daysAway} day${next.daysAway === 1 ? "" : "s"}`
      : null;

  return (
    <GlassCard padding={18}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0">
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Weekly rhythm</p>
          <h3 className="mnf-serif" style={{ fontSize: 19, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}>
            Sabbath rest day
          </h3>
        </div>
        {trailingNote && (
          <PillTag tone={isToday ? "gold" : "neutral"}>{trailingNote}</PillTag>
        )}
      </div>

      <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55, marginBottom: 14 }}>
        Choose one day each week to rest from logging, tracking, and striving. Your streaks pause, never break. A devotional walks with you.
      </p>

      <ToggleRow
        testId="toggle-sabbath-enabled"
        label="Honor a Sabbath each week"
        sub="When off, every day looks the same."
        active={enabled}
        onChange={setEnabled}
      />

      {enabled && (
        <>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", marginTop: 16, marginBottom: 8 }}>
            My day of rest
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {DAYS.map((d) => (
              <DayChip
                key={d.value}
                short={d.short}
                active={day === d.value}
                onClick={() => setDay(d.value)}
                testId={`chip-sabbath-${d.short.toLowerCase()}`}
              />
            ))}
          </div>

          <ToggleRow
            testId="toggle-sabbath-pause-streaks"
            label="Pause streaks on my Sabbath"
            sub='Your streak chip reads "Resting" instead of counting.'
            active={pauseStreaks}
            onChange={setPauseStreaks}
          />
          <div style={{ height: 10 }} />
          <ToggleRow
            testId="toggle-sabbath-devotional"
            label="Show the devotional automatically"
            sub="A short reflection greets you on your rest day."
            active={devotional}
            onChange={setDevotional}
          />
        </>
      )}

      <div className="flex items-center gap-3 mt-5">
        <Btn variant="primary" size="md" onClick={onSave} className="flex-1" data-testid="button-save-sabbath">
          {savedFlash ? "Saved" : `Set Sabbath to ${dayLabel}`}
        </Btn>
      </div>

      <div
        className="mnf-body italic"
        style={{
          marginTop: 16,
          paddingTop: 14,
          borderTop: "1px solid rgba(30,74,53,0.10)",
          fontSize: 12,
          color: "var(--mnf-tg)",
          lineHeight: 1.55,
          textAlign: "center",
        }}
      >
        "The Sabbath was made for you, sister, not you for the Sabbath."
        <span className="block mnf-eyebrow" style={{ color: "var(--mnf-t)", marginTop: 6 }}>
          Mark 2:27, gently paraphrased
        </span>
      </div>
    </GlassCard>
  );
}

function DayChip({
  short,
  active,
  onClick,
  testId,
}: {
  short: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="hover-elevate"
      style={{
        padding: "8px 14px",
        borderRadius: 999,
        border: active ? "1.5px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.16)",
        background: active ? "var(--mnf-f)" : "#fff",
        color: active ? "#F5F0E8" : "var(--mnf-fd)",
        fontFamily: "inherit",
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: "0.04em",
        cursor: "pointer",
        minWidth: 56,
      }}
    >
      {short}
    </button>
  );
}

function ToggleRow({
  label,
  sub,
  active,
  onChange,
  testId,
}: {
  label: string;
  sub: string;
  active: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <button
      onClick={() => onChange(!active)}
      data-testid={testId}
      aria-pressed={active}
      className="w-full flex items-center justify-between gap-3 hover-elevate"
      style={{
        padding: "12px 14px",
        borderRadius: 14,
        border: "1px solid rgba(30,74,53,0.10)",
        background: "#fff",
        textAlign: "left",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <span className="flex-1 min-w-0">
        <span className="mnf-serif block" style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}>
          {label}
        </span>
        <span className="mnf-body block" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4, marginTop: 2 }}>
          {sub}
        </span>
      </span>
      <SwitchPip active={active} />
    </button>
  );
}

function SwitchPip({ active }: { active: boolean }) {
  return (
    <span
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.18)",
        position: "relative",
        transition: "background 180ms ease",
        flexShrink: 0,
        display: "inline-block",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: active ? 18 : 2,
          width: 18,
          height: 18,
          borderRadius: 999,
          background: "#F5F0E8",
          transition: "left 180ms ease",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </span>
  );
}
